import { Component, effect, inject, OnDestroy } from '@angular/core';
import { AlertStyleDirective } from '../../../directives/alert-style.directive';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { FileUploadModule } from 'primeng/fileupload';
import { HeaderComponent } from '../../atoms/header/header.component';
import { NgForOf, NgIf } from '@angular/common';
import { MessageService } from 'primeng/api';
import { GlobalsStore } from '../../../stores/globals.state';
import { GeoDataService } from '../../../services/geo-data/geo-data.service';
import { TypeDropdownComponent } from '../../atoms/type-dropdown/type-dropdown.component';
import { POI, DataLayerType } from '../../../models/data-layer-type';
import { PoiImportValidatorService } from '../../../services/data-layer-type/poi-import-validator.service';
import { Severity } from '../../../models/severity';
import { ImportPreviewBoxComponent } from '../../molecules/import-preview-box/import-preview-box.component';
import { MaskComponent } from '../mask/mask.component';
import { SidebarModule } from 'primeng/sidebar';
import { AppUtilityService } from '../../../utility/app.utility';
import { PoiImportListComponent } from '../../molecules/poi-import-list/poi-import-list.component';
import { GeoData } from '../../../models/geo-data';
import { BasicMapComponent } from '../../molecules/basic-map/basic-map.component';
import { GeoDataStore } from '../../../stores/geo/geo-data.state';
import { FeatureFormComponent } from '../../molecules/feature-form/feature-form.component';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { GeoJSON } from 'leaflet';
import { LayerDropdownComponent } from '../../atoms/layer-dropdown/layer-dropdown.component';
import { DataLayer } from '../../../models/data-layer';
import { PanelModule } from 'primeng/panel';
import { MenuModule } from 'primeng/menu';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { getState, type } from '@ngrx/signals';
import { DownloadTemplateComponent } from '../../atoms/download-template/download-template.component';
import { PoiExportConverterService } from '../../../services/data-layer-type/poi-export-converter.service';
import { ActivatedRoute } from '@angular/router';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';

@Component({
  selector: 'app-import-export-poi',
  standalone: true,
  imports: [
    AlertStyleDirective,
    ButtonModule,
    DividerModule,
    FileUploadModule,
    HeaderComponent,
    NgIf,
    TypeDropdownComponent,
    ImportPreviewBoxComponent,
    NgForOf,
    MaskComponent,
    SidebarModule,
    PoiImportListComponent,
    BasicMapComponent,
    FeatureFormComponent,
    ScrollPanelModule,
    LayerDropdownComponent,
    PanelModule,
    MenuModule,
    AlertComponent,
    DownloadTemplateComponent,
  ],
  templateUrl: './import-export-poi.component.html',
  styleUrl: './import-export-poi.component.scss',
})
export class ImportExportPoiComponent implements OnDestroy {
  readonly globalsStore = inject(GlobalsStore);
  readonly dataLayerTypeStore = inject(DataLayerTypeStore);
  readonly geoStore = inject(GeoDataStore);

  icon = '';
  uploadData: any = undefined;
  poiToImport: POI[] = [];
  itemsToShowInOverview: POI[] = [];
  selectedType: DataLayerType | null = null;
  selectedLayer: DataLayer | null = null;
  converting = false;
  poiGeoData?: GeoData;
  poiPreview: GeoJSON.FeatureCollection<
    GeoJSON.Point | GeoJSON.Polygon,
    GeoJSON.GeoJsonProperties
  > | null = null;

  constructor(
    protected geoDataService: GeoDataService,
    private messageService: MessageService,
    private utilityService: AppUtilityService,
    private poiImportValidatorService: PoiImportValidatorService,
    private poiExportConverterService: PoiExportConverterService,
    protected activatedRoute: ActivatedRoute
  ) {
    effect(() => {
      const geoState = getState(this.geoStore);
      const globalState = getState(this.globalsStore);

      if (geoState.selectedId && !this.poiToImport.length) {
        this.poiPreview = this.geoStore.geoJsonFromSelection();
      } else if (!this.poiToImport.length) {
        this.poiPreview = null;
      }

      if (!this.selectedType) {
        this.selectedType =
          globalState.selectedType && globalState.selectedType.type === 'Point'
            ? globalState.selectedType
            : null;
      }

      if (!this.selectedLayer) {
        this.selectedLayer = globalState.selectedLayer
          ? globalState.selectedLayer
          : null;
      }
    });
  }

  validationPreviewSeverities: Severity[] = [
    'info',
    'success',
    'warning',
    'error',
  ];

  /**
   * Filters the list of points of interest (POI) by their validation severity.
   *
   * @param {Severity} severity - The severity level to filter the POI by.
   * @return {POI[]} - An array of POI objects that match the specified severity level.
   */
  getItemsBy(severity: Severity): POI[] {
    return this.poiToImport.filter(poi => {
      return poi.validation === severity;
    });
  }

  /**
   * Sets the items to display based on the provided severity level.
   *
   * @param {Severity} severity - The severity level used to determine the items to display.
   * @return {void} This method does not return a value.
   */
  setItemsToShow(severity: Severity): void {
    this.setIconByState(severity);
    this.itemsToShowInOverview = this.getItemsBy(severity);
  }

  /**
   * Checks if there are any Points of Interest (POIs) to import with a validation status of 'warning'.
   *
   * @return {boolean} True if there is at least one POI with a 'warning' validation status, otherwise false.
   */
  get hasWarnings() {
    return (
      this.poiToImport.filter(poi => {
        return poi.validation === 'warning';
      }).length > 0
    );
  }

  /**
   * Closes the overview by clearing the items to be displayed and resetting the icon.
   *
   * @return {void} Does not return a value.
   */
  closeOverview(): void {
    this.itemsToShowInOverview = [];
    this.icon = '';
  }

  /**
   * Handles the file upload event, processes the uploaded data, imports and validates points of interest (POIs),
   * and prepares a preview of the POIs. Additionally, displays a success message upon completion.
   *
   * @param {any} event The upload event containing the file data in its `originalEvent.body`.
   * @return {void} This method does not return a value.
   */
  onUpload(event: any): void {
    this.uploadData = event.originalEvent.body;
    this.poiToImport = this.validatePOIs(
      JSON.parse(JSON.stringify(event.originalEvent.body))
    ); // deepcopy

    this.setPOIPreview(this.poiToImport);

    this.messageService.add({
      severity: 'info',
      summary: 'Upload erfolgreich!',
    });
  }

  /**
   * Validates and maps a list of Points of Interest (POIs) using the POI import validator service.
   *
   * @param {POI[]} poiList - The list of POIs to be validated and mapped.
   * @return {POI[]} The result of the validation and mapping process performed by the service.
   */
  validatePOIs(poiList: POI[]): POI[] {
    return this.poiImportValidatorService.validateAndMap(
      poiList,
      this.geoStore.keysFromSelection()
    );
  }

  /**
   * Sets the icon property based on the given severity state.
   *
   * @param {Severity} severity - The severity state based on which the icon is set.
   * @return {void} This method does not return a value.
   */
  setIconByState(severity: Severity): void {
    this.icon = this.utilityService.getSeverityIcon(severity);
  }

  /**
   * Converts the address data from `poiToImport` into geographical coordinates.
   * Updates the state of conversion and processes the data for preview.
   * Handles errors during the conversion process and ensures that
   * the `converting` flag is appropriately reset upon completion.
   *
   * @return {void} This method does not return any value.
   */
  convertAddresses(): void {
    this.converting = true;
    this.geoDataService
      .convertAddressesToCoordinates(this.poiToImport)
      .subscribe({
        error: error => {
          console.error('Error during conversion:', error);
        },
        complete: () => {
          this.converting = false;
          this.poiToImport = this.validatePOIs(
            JSON.parse(JSON.stringify(this.poiToImport))
          );
          this.setPOIPreview(this.poiToImport);
        },
      });
  }

  /**
   * Sets the preview data for POIs (Points of Interest) by converting the given POIs to GeoJSON format
   * and storing them in the relevant preview property.
   *
   * @param {POI[]} poi - An array of POI (Points of Interest) objects to be processed for preview.
   * @return {void} No return value.
   */
  setPOIPreview(poi: POI[]): void {
    this.poiGeoData = this.geoDataService.convertPoiToGeoData(poi);

    this.poiPreview = this.poiGeoData.geoJson
      ? JSON.parse(this.poiGeoData.geoJson)
      : null;
  }

  /**
   * Cancels the current operation by resetting the points of interest (POI) to import
   * and updating the preview with the selected geographical data.
   *
   * @return {void} Does not return any value.
   */
  cancel(): void {
    this.poiToImport = [];
    this.poiPreview = this.geoStore.geoJsonFromSelection();
  }

  /**
   * Exports the currently selected geographic data by converting it into an XLSX format.
   *
   * The method retrieves the selected geographic data in JSON format, processes it
   * into an exportable array, and converts it to an XLSX file format.
   *
   * @return {void} This method does not return a value, as it triggers the conversion and export process.
   */
  exportSelected(): void {
    const selectedJSON = this.geoStore.geoJsonFromSelection();
    if (!selectedJSON) return;
    this.geoStore.convertToXlsx({
      poiRows:
        this.poiExportConverterService.exportGeoJSONToExportArray(selectedJSON),
      filename:
        `Standorte_${this.selectedType?.name}_${this.selectedLayer?.year}` ||
        'standort_export',
    });
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * It performs cleanup by invoking the `cancel` method to release any resources
   * or terminate active processes.
   *
   * @return {void} Does not return a value.
   */
  ngOnDestroy(): void {
    this.cancel();
  }

  protected readonly type = type;
}
